import { ActionType } from 'typesafe-actions';
import { put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';

import {
  PASS_SIGNATURE_VALIDATION,
  INVALID_DOCUMENT_SIGNATURE,
  ADD_KNM_DOCUMENT,
  DONT_HAVE_INTERNET_CONNECTION,
} from 'CorePackage/MainModule/constants/notices';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import {
  fetchKnmDocumentAttachmentDownload,
  fetchKnmDocumentAttachmentUpload,
  fetchKnmDocumentSignaturesUpload,
  fetchZipKnmAttachmentDownloadWithSig,
} from 'APIPackage/KnmModule/services/knmDocumentAttachmentApi';
import { IKnmFormAttachment } from 'KnmPackage/MainModule/interfaces/IKnmFormAttachment';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import {
  knmDocumentsDownload,
  knmDocumentsSaveSignedDocument,
  knmDocumentsUpload,
} from '../actions/knmDocumentsActions';
import { convertFileToBase64 } from '../common/convertFileToBase64';
import { convertSignFileToBase64 } from '../common/convertSignFileToBase64';
import { KnmDocumentsActionsEnum } from '../enums/KnmDocumentsActionsEnum';
import { IKnmDocumentsForm } from '../interfaces/IKnmDocumentsForm';
import { getKnmDataId } from '../../MainModule/selectors/knmSelectors';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';

export function* watchKnmDocumentsSaga() {
  yield takeEvery(KnmDocumentsActionsEnum.KNM_DOCUMENTS_UPLOAD, knmDocumentsUploadSaga);
  yield takeEvery(KnmDocumentsActionsEnum.KNM_DOCUMENTS_SAVE_SIGNED_DOCUMENT, knmDocumentsSaveSignedDocumentSaga);
  yield takeEvery(KnmDocumentsActionsEnum.KNM_DOCUMENTS_DOWNLOAD, knmDocumentsDownloadSaga);
}

function* knmDocumentsDownloadSaga(action: ActionType<typeof knmDocumentsDownload>) {
  function* funcTry() {
    const { documents, itemsCheck } = action.payload;
    const state: IRootState = yield select();
    const knmId = getKnmDataId(state);
    const docs = documents.filter(doc => doc.guid && itemsCheck[doc.guid]);

    if (docs.length) {
      const attachments = docs.map(({ fileName, filePath, fileSize, mimeType, signatureInfo, bucketName }) => ({
        fileName,
        filePath,
        fileSize,
        mimeType,
        signatureInfo,
        bucketName,
      }));

      if (attachments.length === 1 && !attachments[0].signatureInfo) {
        const attachmentFileData: Blob = yield fetchKnmDocumentAttachmentDownload({ attachment: attachments[0] });

        if (attachmentFileData) {
          saveFile(attachmentFileData, attachments[0].fileName || '');
        }
      } else if (knmId) {
        const attachmentsFileData: Blob = yield fetchZipKnmAttachmentDownloadWithSig({
          knmId,
          attachments,
        });

        if (attachmentsFileData) {
          saveFile(attachmentsFileData, 'Список сопроводительных документов.zip');
        }
      }
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmDocumentsSaveSignedDocumentSaga(action: ActionType<typeof knmDocumentsSaveSignedDocument>) {
  function* funcTry() {
    const { form, signedDocument, signatureInfo, formFieldName, addAttachment } = action.payload;
    const listId = uuidv4();

    formFieldName &&
      addAttachment(formFieldName, {
        fileName: signedDocument.name,
        fileSize: Math.trunc(signedDocument.size / 1000) || 0,
        isNew: true,
        listId,
        file: signedDocument,
      });
    const attachment: IKnmAttachment = yield fetchKnmDocumentAttachmentUpload({ file: signedDocument });

    if (attachment) {
      let attachmentIndex = -1;
      const documents = form?.getFieldState('documents')?.value as IKnmDocumentsForm['documents'] | undefined;
      const documentIndex = documents?.findIndex(document => {
        const documentAttachmentIndex = document?.attachments?.findIndex(item => item.listId === listId);

        if (typeof documentAttachmentIndex === 'number' && documentAttachmentIndex >= 0) {
          attachmentIndex = documentAttachmentIndex;

          return true;
        }

        return false;
      });

      if (documents && typeof documentIndex === 'number' && documentIndex >= 0 && attachmentIndex >= 0) {
        const newDocuments: IKnmDocumentsForm['documents'] = [...documents];
        const changingDocument = documents[documentIndex];
        const changingDocumentAttachments = changingDocument?.attachments;

        if (changingDocument && changingDocumentAttachments) {
          const newAttachments: IKnmFormAttachment[] = [...changingDocumentAttachments];

          newAttachments[attachmentIndex] = {
            ...newAttachments[attachmentIndex],
            ...attachment,
            signatureInfo,
            signed: signatureInfo ? true : null,
            isNew: true,
          };
          newDocuments[documentIndex] = {
            ...changingDocument,
            attachments: newAttachments,
          };
        }
        form?.change('documents', newDocuments);
      }
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

const checkOnline = async (): Promise<boolean> => {
  try {
    await fetch('/public/api/access-manager/api/users/current/extendedprofile');

    return true;
  } catch (_e) {
    return false;
  }
};

function* knmDocumentsUploadSaga(action: ActionType<typeof knmDocumentsUpload>) {
  function* funcTry() {
    const { document, signs, onSaveSignedDocument } = action.payload;

    const isOnline: boolean = yield checkOnline();

    if (!isOnline) {
      yield put(
        addNoticeAction({
          renderContent: DONT_HAVE_INTERNET_CONNECTION,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
        }),
      );

      return;
    }

    if (document && signs.length) {
      const convertedDocument: string = yield convertFileToBase64(document);
      const signTextArr: string[] = [];

      // eslint-disable-next-line no-restricted-syntax
      for (const sign of signs) {
        const signText: string = yield convertSignFileToBase64(sign);

        signTextArr.push(signText);
      }

      const files = {
        document: convertedDocument,
        signatures: signTextArr,
      };
      const res: IKnmAttachment = yield fetchKnmDocumentSignaturesUpload(files);

      if (res && res.signed) {
        yield put(
          addNoticeAction({
            renderContent: PASS_SIGNATURE_VALIDATION,
            noticeType: NoticeTypesEnum.SUCCESS,
            messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
          }),
        );
        onSaveSignedDocument(document, res.signatureInfo);
      } else {
        yield put(
          addNoticeAction({
            renderContent: INVALID_DOCUMENT_SIGNATURE,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
          }),
        );
      }
    } else if (document) {
      // ждём фактической загрузки файла внутри колбэка
      yield onSaveSignedDocument(document, null);
      yield put(
        addNoticeAction({
          renderContent: ADD_KNM_DOCUMENT,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.UPLOAD_DOCUMENT,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}
